import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { useFormContext, useController } from "react-hook-form";
import { Check } from "lucide-react";

import {
  FieldBaseProps,
  useFieldProps,
} from "@shared/features/form/useFieldProps";

export type CheckboxProps = FieldBaseProps & {
  name?: string;
  id?: string;
  disabled?: boolean;
  value?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  ariaLabel?: string;
  onFocus?: () => void;
  onBlur?: () => void;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      name,
      id,
      disabled,
      value: controlledValue,
      onCheckedChange,
      required,
      error,
      isLoading,
      isSubmitting,
      className,
      ariaLabel,
      onFocus,
      onBlur,
    },
    ref
  ) => {
    const form = useFormContext();
    const isRHF = name && form?.control && !onCheckedChange;

    let rhfValue = false;
    let rhfOnChange = (_: any) => {};

    if (isRHF) {
      const {
        field: { value, onChange },
      } = useController({
        name,
        control: form.control,
        defaultValue: false,
        rules: {
          required: required ? "This field is required" : false,
        },
      });

      rhfValue = value;
      rhfOnChange = onChange;
    }

    const fieldProps = useFieldProps({
      className,
      error,
      isLoading,
      isSubmitting,
      required,
    });

    const isChecked = isRHF ? rhfValue : controlledValue ?? false;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      if (isRHF) {
        rhfOnChange(checked);
      } else {
        onCheckedChange?.(checked);
      }
    };

    return (
      <label
        htmlFor={id ?? name}
        className={twMerge(
          "inline-flex items-center cursor-pointer",
          fieldProps.className,
          "border-none shadow-none pl-0"
        )}
      >
        <input
          id={id ?? name}
          ref={ref}
          type="checkbox"
          className="peer absolute opacity-0 w-5 h-5"
          checked={isChecked}
          onChange={handleChange}
          data-testid={`checkbox-${name}`}
          disabled={disabled}
          name={name}
          aria-label={ariaLabel}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        <div
          className={twMerge(
            "h-5 w-5 shrink-0 rounded-[4px] border shadow text-transparent",
            "border-brand-800 peer-checked:bg-brand-800 peer-checked:text-brand-50 peer-checked:border-0",
            "disabled:cursor-not-allowed disabled:bg-brand-200 disabled:border-brand-300",
            "disabled:peer-checked:bg-brand-200 disabled:peer-checked:border-brand-300 disabled:peer-checked:text-brand-400"
          )}
        >
          <div className="flex items-center justify-center">
            <Check className="h-5 w-5" />
          </div>
        </div>
      </label>
    );
  }
);

export default Checkbox;
